import { useState, useEffect } from "react";
import { QUIZ_QUESTIONS } from "../constants/quizQuestionData";
import QuizQuestion from "./QuizQuestion";
import "../css/Quiz.css";
import "../css/Main.css";
import axios from "axios";
import { getUserID } from "../utils/UserIdentifer";
import { QuizResponse } from "../types/QuizSubmission";
import { v4 as uuidv4 } from 'uuid';

// Create an axios instance using the base path
const axiosApi = axios.create({
  baseURL: import.meta.env.BASE_URL
});

interface QuizProps {
  onComplete: (responses: QuizResponse[]) => void;
}

const getBlankAnswers = (): number[][] => {
  return QUIZ_QUESTIONS.map((q) => new Array(q.subQuestions.length).fill(-1));
}

const getBlankSubmittedQuestions = (): boolean[] => {
  return Array(QUIZ_QUESTIONS.length).fill(false);
}

const Quiz: React.FC<QuizProps> = ({ onComplete }) => {
  const loadSavedState = () => {
    const savedIndex = localStorage.getItem('currentQuestionIndex');
    const savedAnswers = localStorage.getItem('allSelectedAnswers');
    const savedSubmitted = localStorage.getItem('submittedQuestions');
    const savedSubmissionId = localStorage.getItem('submissionId');

    if (savedIndex && savedAnswers && savedSubmitted && savedSubmissionId) {
      const parsedIndex = parseInt(savedIndex);
      const parsedAnswers = JSON.parse(savedAnswers);
      const parsedSubmitted = JSON.parse(savedSubmitted);

      if (parsedIndex >= 0 && parsedIndex < QUIZ_QUESTIONS.length &&
          parsedAnswers.length === QUIZ_QUESTIONS.length &&
          parsedSubmitted.length === QUIZ_QUESTIONS.length) {
        return { 
          index: parsedIndex, 
          answers: parsedAnswers, 
          submitted: parsedSubmitted,
          submissionId: savedSubmissionId
        };
      }
    }
    return null;
  };

  const savedState = loadSavedState();
  const [submissionId, _setSubmissionId] = useState(savedState?.submissionId || uuidv4());
  const [userID, setUserID] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(savedState?.index ?? 0);
  const [allSelectedAnswers, setAllSelectedAnswers] = useState<number[][]>(savedState?.answers ?? getBlankAnswers());
  const [showingFeedback, setShowingFeedback] = useState(false);
  const [submittedQuestions, setSubmittedQuestions] = useState<boolean[]>(savedState?.submitted ?? getBlankSubmittedQuestions());

  useEffect(() => {
    setUserID(getUserID());
  }, []);

  useEffect(() => {
    localStorage.setItem('currentQuestionIndex', currentQuestionIndex.toString());
    localStorage.setItem('allSelectedAnswers', JSON.stringify(allSelectedAnswers));
    localStorage.setItem('submittedQuestions', JSON.stringify(submittedQuestions));
    localStorage.setItem('submissionId', submissionId);
  }, [currentQuestionIndex, allSelectedAnswers, submittedQuestions, submissionId]);

  // Initialize showing feedback based on submitted state when navigating
  useEffect(() => {
    setShowingFeedback(submittedQuestions[currentQuestionIndex]);
  }, [currentQuestionIndex, submittedQuestions]);

  const allQuestionsAnswered = submittedQuestions.every(submitted => submitted);
  const onLastQuestion = currentQuestionIndex === QUIZ_QUESTIONS.length - 1;
  const currentQuestionAnswered = allSelectedAnswers[currentQuestionIndex].every(
    (answer) => answer !== -1
  );
  const currentQuestionSubmitted = submittedQuestions[currentQuestionIndex];

  const handleNext = () => {
    if (currentQuestionIndex < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else if (allQuestionsAnswered) {
      handleFinalSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    if (currentQuestionSubmitted) return; // Prevent changing answers after submission

    const newAllSelectedAnswers = [...allSelectedAnswers];
    newAllSelectedAnswers[currentQuestionIndex] = [
      ...newAllSelectedAnswers[currentQuestionIndex],
    ];
    // Toggle selection: if already selected, deselect it (-1), otherwise select it
    const currentSelection = newAllSelectedAnswers[currentQuestionIndex][questionIndex];
    newAllSelectedAnswers[currentQuestionIndex][questionIndex] = 
      currentSelection === answerIndex ? -1 : answerIndex;
    setAllSelectedAnswers(newAllSelectedAnswers);
  };

  const handleSubmitQuestion = () => {
    // Mark current question as submitted
    const newSubmittedQuestions = [...submittedQuestions];
    newSubmittedQuestions[currentQuestionIndex] = true;
    setSubmittedQuestions(newSubmittedQuestions);
    
    // Show feedback for the current question
    setShowingFeedback(true);
    
    // Submit this question's response to the server
    const currentQuestion = QUIZ_QUESTIONS[currentQuestionIndex];
    const response = {
      questionIdentifier: currentQuestion.identifier,
      selectedAnswers: allSelectedAnswers[currentQuestionIndex],
      correctAnswers: currentQuestion.subQuestions.map(
        (subQuestion) => subQuestion.correct_answer
      ),
    };
    
    try {
      axiosApi.post("/api/submit", {
        submission_id: submissionId,
        user_identifier: userID,
        response: response
      });
    } catch (error) {
      console.error("Error submitting response:", error);
    }
  };

  const handleButtonAction = () => {
    if (currentQuestionSubmitted) {
      // If the current question is submitted, clicking will navigate to next question
      handleNext();
    } else {
      // If the current question is not submitted, clicking will submit it
      handleSubmitQuestion();
    }
  };

  const responses: QuizResponse[] = allSelectedAnswers.map((answers, index) => ({
    questionIdentifier: QUIZ_QUESTIONS[index].identifier,
    selectedAnswers: answers,
    correctAnswers: QUIZ_QUESTIONS[index].subQuestions.map(
      (subQuestion) => subQuestion.correct_answer
    ),
  }));

  const handleFinalSubmit = () => {
    // Since we're submitting after each question, we just need to notify
    // the parent component that the quiz is complete
    onComplete(responses);
  };

  // Add a function to check if an answer is correct
  const isAnswerCorrect = (questionIndex: number): boolean => {
    if (!submittedQuestions[questionIndex]) return false;
    
    const selectedAnswers = allSelectedAnswers[questionIndex];
    const correctAnswers = QUIZ_QUESTIONS[questionIndex].subQuestions.map(
      subQ => subQ.correct_answer
    );
    
    // Check if all selected answers match correct answers
    return selectedAnswers.every((answer, idx) => answer === correctAnswers[idx]);
  };

  return (
    <div style={{ textAlign: "center" }}>
      <h3 className="question-title content-container">
        {QUIZ_QUESTIONS[currentQuestionIndex].title}
      </h3>

      <div className="nav-container">
        <button
          onClick={handlePrevious}
          className={`nav-button`}
          disabled={currentQuestionIndex === 0}
        >
          ←
        </button>

        <div className="nav-dots-container">
          {allSelectedAnswers.map((_, index) => {
            const isCorrect = isAnswerCorrect(index);
            const isSubmitted = submittedQuestions[index];
            
            return (
              <span
                className={`question-indicator 
                  ${isSubmitted 
                    ? (isCorrect ? "correct" : "incorrect") 
                    : "incomplete"}
                  ${index === currentQuestionIndex ? "current" : ""}`}
                key={index}
                onClick={() => {
                  setCurrentQuestionIndex(index);
                }}
              />
            );
          })}
        </div>

        <button
          onClick={handleNext}
          className={`nav-button ${currentQuestionSubmitted ? "ready" : ""}`}
          disabled={!currentQuestionSubmitted}
        >
          {onLastQuestion && allQuestionsAnswered ? "Done" : "→"}
        </button>
      </div>

      <div className="quiz-container content-container">
        <QuizQuestion
          key={`${QUIZ_QUESTIONS[currentQuestionIndex].identifier}-${showingFeedback}`}
          question={QUIZ_QUESTIONS[currentQuestionIndex]}
          selectedAnswers={allSelectedAnswers[currentQuestionIndex]}
          onAnswerSelect={handleAnswerSelect}
          isSubmitted={currentQuestionSubmitted}
          showFeedback={showingFeedback}
          isFinal={false}
          onButtonClick={handleButtonAction}
          isButtonDisabled={!currentQuestionAnswered && !currentQuestionSubmitted}
          isLastQuestion={onLastQuestion && allQuestionsAnswered}
        />
      </div>
    </div>
  );
};

export default Quiz;
